/* eslint-disable @typescript-eslint/no-explicit-any */
import {DestroyRef, Injectable} from '@angular/core';
import {LookupService} from './lookup.service';
import {PlanModelService} from './plan-model.service';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {catchError, of, throwError} from 'rxjs';
import {SpatialUtilService} from '../../design/services/spatial-util.service';
import {filter, switchMap} from 'rxjs/operators';
import {InterestService} from '../../design/services/features/interest.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AffectedLotReconciliationService} from './affected-lot-reconciliation.service';
import {PDF_CREATION_METHOD} from '../constants';
import {RoadReconciliationService} from '../../design/services/road-reconciliation.service';

@Injectable({
    providedIn: 'root'
})
export class InterestReconciliationService {
    title = 'InterestReconciliationService';

    constructor(
        private affectedLotReconciliationService: AffectedLotReconciliationService,
        private destroyRef: DestroyRef,
        private interestService: InterestService,
        private lookupService: LookupService,
        private planModelService: PlanModelService,
        private eventBus: NgEventBus,
        private spatialUtilService: SpatialUtilService,
        private roadReconciliationService: RoadReconciliationService
    ) {
        this.eventBus
            .on('openInterestModal')
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((meta: MetaData) => {
                    console.debug(`${this.title} - on - openInterestModal`);
                    const spatialInterestAttributes = meta.data;

                    if (spatialInterestAttributes) {
                        const spatialInterest = this.spatialUtilService.findById('interest', spatialInterestAttributes.id);
                        const formerTenureInterests = this.planModelService.getModel().formerTenures;
                        const newInterests = this.planModelService.getModel().interests;
                        const interest = this.interestService.findNonSpatialInterest(spatialInterest, newInterests, formerTenureInterests);

                        const observable =
                            spatialInterest.attributes.type === 1
                                ? this.interestService.openTenureInterestsNotificationsModal(interest)
                                : this.interestService.openNewInterestModal(interest);
                        return observable.pipe(
                            switchMap(() => this.reconcileInterests(formerTenureInterests, newInterests)),
                            switchMap(() => this.affectedLotReconciliationService.process())
                        );
                    } else {
                        return this.interestService.openNewInterestModal(null);
                    }
                })
            )
            .subscribe();
        this.eventBus
            .on('planLodgementService:plan-saved')
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(eventData => eventData.data === true),
                switchMap(() => {
                    return this.reconcileInterests(this.planModelService.getModel().formerTenures, this.planModelService.getModel().interests).pipe(
                        switchMap(() => this.affectedLotReconciliationService.process()),
                        switchMap(() => {
                            const isRoadWidening =
                                this.planModelService.getModel().pdfCreationMethod === PDF_CREATION_METHOD.SURVEYOR_PORTAL &&
                                this.planModelService.getModel().roadWidening;
                            const isRoadLGA =
                                this.planModelService.getModel().pdfCreationMethod === PDF_CREATION_METHOD.SURVEYOR_PORTAL &&
                                this.planModelService.getModel().lgaApprovedUnnamedRoad;
                            return this.roadReconciliationService.reconcileRoads(
                                this.planModelService.getModel().roads,
                                this.planModelService.getModel().exemptedRoads,
                                isRoadWidening,
                                isRoadLGA
                            );
                        })
                    );
                })
            )
            .subscribe();
    }

    spatialInterestEqualToNonSpatialInterest(spatialInterest, nonSpatialInterest) {
        return (
            spatialInterest.interestid === nonSpatialInterest.interestId &&
            spatialInterest.type === nonSpatialInterest.type &&
            ((!spatialInterest.subject && !nonSpatialInterest.subject) || spatialInterest.subject === nonSpatialInterest.subject) &&
            ((!spatialInterest.description && !nonSpatialInterest.description) || spatialInterest.description === nonSpatialInterest.description) &&
            ((!spatialInterest.tenure && !nonSpatialInterest.tenure) || spatialInterest.tenure === nonSpatialInterest.tenure) &&
            ((!spatialInterest.portion && !nonSpatialInterest.portion) || spatialInterest.portion === nonSpatialInterest.portion) &&
            spatialInterest.burdenlots === nonSpatialInterest.burdenLots.join() &&
            spatialInterest.beneficiarylots === nonSpatialInterest.beneficiaryLots.join() &&
            spatialInterest.scope === nonSpatialInterest.scope
        );
    }

    getInterestLots(lotRanges) {
        console.debug(`${this.title} - getInterestLots`);

        return lotRanges.reduce((acc, v) => {
            const startLot = v.startLot;
            const endLot = v.endLot || v.startLot;
            for (let lot = startLot; lot <= endLot; lot++) {
                acc.push(lot);
            }
            return acc;
        }, []);
    }

    getNonSpatialInterests(formerTenures, interests) {
        console.debug(`${this.title} - getNonSpatialInterests`);

        const output = formerTenures
            .filter(formerTenure => formerTenure.formerTenureNotifications && formerTenure.formerTenureNotifications.length)
            .reduce((acc, formerTenure) => {
                const filteredFormerTenureNotifications = formerTenure.formerTenureNotifications.filter(
                    ftn => ftn.id && ftn.digital && ftn.subjects && ftn.subjects.length
                );
                acc.push(...filteredFormerTenureNotifications);
                return acc;
            }, [])
            .reduce((acc, formerTenureNotification) => {
                const formerTenureNotificationInterests = formerTenureNotification.subjects
                    .filter(subject => subject.id)
                    .map(subject => {
                        const effectiveNotificationCode =
                            formerTenureNotification.notificationCodeOverride || formerTenureNotification.notificationCode;

                        const interestData = {
                            burdenLots: [],
                            beneficiaryLots: [],
                            description: formerTenureNotification.endorsement,
                            tenure: formerTenureNotification.identifier,
                            interestcode: effectiveNotificationCode,
                            interestId: subject.id,
                            portion: subject.subjectType && subject.subjectType.description,
                            subject: subject.subject || null,
                            type: 1
                        };

                        const lots = this.getInterestLots(subject.affectedLots);

                        if (
                            this.lookupService
                                .getLookups('formerTenureNotification')
                                .some(v => v.benefit && v.notificationCode === effectiveNotificationCode)
                        ) {
                            interestData.beneficiaryLots = lots;
                        } else {
                            interestData.burdenLots = lots;
                        }
                        return interestData;
                    });
                acc.push(...formerTenureNotificationInterests);
                return acc;
            }, []);

        output.push(
            ...interests
                .filter(interest => interest.id)
                .map(interest => ({
                    interestId: interest.id,
                    interestcode: interest.interestCode?.code,
                    type: 2,
                    subject: interest.subject || null,
                    description:
                        interest.interestCode?.purpose +
                        (interest.interestCode?.statutoryReference ? ' ' + interest.interestCode?.statutoryReference : ''),
                    tenure: '',
                    burdenLots: (interest.burdenedLots && this.getInterestLots(interest.burdenedLots)) || null,
                    beneficiaryLots:
                        (interest.benefits &&
                            interest.benefits.reduce((acc, benefit) => {
                                const benefitLots = this.getInterestLots(benefit.lots);
                                acc.push(...benefitLots);
                                return acc;
                            }, [])) ||
                        null
                }))
        );

        return output;
    }

    reconcileInterests(formerTenures = [], interests = []) {
        console.info(`${this.title} - reconcileInterestsAndNotifications`);

        if (this.planModelService.getModel().pdfCreationMethod !== 'SURVEYOR_PORTAL') {
            return of(null);
        }

        const interestsToBeAdded = [];
        const interestsToBeUpdated = [];
        const interestsToBeDeleted = [];
        const spatialInterests = [];
        const spatialInterestIds = [];
        let spatialInterestAssociationMap: any;

        const nonSpatialInterests = this.getNonSpatialInterests(formerTenures, interests);

        const nonSpatialInterestMap = nonSpatialInterests.reduce((acc, nsi) => {
            acc[nsi.interestId] = nsi;
            return acc;
        }, {});

        const noActionNotRequiredNotifications = this.planModelService
            .getModel()
            .formerTenures.reduce((acc, formerTenure) => {
                if (formerTenure.formerTenureNotifications) {
                    acc.push(...formerTenure.formerTenureNotifications);
                }
                return acc;
            }, [])
            .filter(
                ftn =>
                    ftn.digital &&
                    ftn.subjects &&
                    ftn.subjects.length &&
                    (!ftn.treatmentAction || ['NO_ACTION', 'NOT_REQUIRED_TO_SHOW'].includes(ftn.treatmentAction.id))
            );

        const clientSpatialInterests = this.spatialUtilService.getInterests();

        this.spatialUtilService.clearInterests();

        return this.spatialUtilService.queryFeatures('interest').pipe(
            switchMap((features: any) => {
                spatialInterests.push(...features.map(f => f.attributes));
                spatialInterestIds.push(...features.map(f => f.attributes.id));

                this.spatialUtilService.getInterests().forEach(f => {
                    const clientSpatialInterest = clientSpatialInterests.find(csi =>
                        [
                            'beneficiarylots',
                            'burdenlots',
                            'description',
                            'guid',
                            'id',
                            'interestcode',
                            'interestid',
                            'objectid',
                            'portion',
                            'tenure',
                            'type'
                        ].every(k => f.attributes[k] === csi.attributes[k])
                    );
                    if (clientSpatialInterest) {
                        // Set subject, polygonids and polylineids attributes back to their preexisting client side values
                        ['subject', 'polygonids', 'polylineids'].forEach(k => (f.attributes[k] = clientSpatialInterest.attributes[k]));
                    }
                });

                const formerTenureNotificationLookups = this.lookupService.getLookups('formerTenureNotification');
                const interestLookups = this.lookupService.getLookups('interest');

                spatialInterestAssociationMap = this.spatialUtilService.getInterests().reduce((acc, f) => {
                    let interestScopePolygon = false;
                    let interestScopePolyline = false;
                    const nonSpatialInterest = nonSpatialInterestMap[f.attributes.interestid];
                    const interestCode = nonSpatialInterest ? nonSpatialInterest.interestcode : f.attributes.interestcode;

                    let types;
                    switch (f.attributes.type) {
                        case 1:
                            types = formerTenureNotificationLookups.filter(l => l.notificationCode === interestCode);
                            break;
                        case 2:
                            types = interestLookups.filter(l => l.code === interestCode);
                            break;
                        default:
                            types = [];
                            break;
                    }

                    if (types.length) {
                        interestScopePolygon = types.every(t => t.scope.includes('POLYGON'));
                        interestScopePolyline = types.every(t => t.scope.includes('POLYLINE'));
                    }

                    acc[f.attributes.id] = {
                        polygonIds: interestScopePolygon ? this.spatialUtilService.parseIdsAttr(f.attributes.polygonids) : [],
                        polylineIds: interestScopePolyline ? this.spatialUtilService.parseIdsAttr(f.attributes.polylineids) : []
                    };

                    if (f.attributes.polygonids && !interestScopePolygon) {
                        f.attributes.polygonids = null;
                    }

                    if (f.attributes.polylineids && !interestScopePolyline) {
                        f.attributes.polylineids = null;
                    }

                    return acc;
                }, {});

                // After restoring a snapshot, need to clean up any annotations, polygons and polylines associated with
                // previously deleted interests
                // Delete annotations associated with previously deleted interests
                const annotationsToDelete = this.spatialUtilService.getAnnotations().filter(a => {
                    const {featuretype: featureType, featureid: featureId, childtype: childType, childid: childId} = a.attributes;

                    if (featureType !== 'interest') {
                        return false;
                    }

                    if (!spatialInterestIds.includes(featureId)) {
                        return true;
                    }

                    const spatialInterestAssociation = spatialInterestAssociationMap[featureId];

                    if (spatialInterestAssociation.polygonIds.length === 0 && spatialInterestAssociation.polylineIds.length === 0) {
                        return true;
                    }

                    return (
                        childId &&
                        ((childType === 'polygon' && !spatialInterestAssociation.polygonIds.includes(childId)) ||
                            (childType === 'polyline' && !spatialInterestAssociation.polylineIds.includes(childId)))
                    );
                });
                if (annotationsToDelete.length) {
                    return this.spatialUtilService.applyEdits('annotation', null, null, annotationsToDelete);
                }
                return of(null);
            }),
            switchMap(() => {
                // Update polygons associated with previously deleted interests
                const polygonsToUpdate = this.spatialUtilService
                    .getPolygons()
                    .filter(p => {
                        const polygonInterestIds = this.spatialUtilService.parseIdsAttr(p.attributes.interestids);
                        return (
                            polygonInterestIds.some(id => !spatialInterestIds.includes(id)) ||
                            !spatialInterestIds.every(
                                id => polygonInterestIds.includes(id) === spatialInterestAssociationMap[id].polygonIds.includes(p.attributes.id)
                            )
                        );
                    })
                    .map(p => {
                        const newInterestIds = spatialInterestIds.filter(id =>
                            spatialInterestAssociationMap[id].polygonIds.includes(p.attributes.id)
                        );
                        const polygonUpdate = this.spatialUtilService.copyGraphic(p);
                        polygonUpdate.attributes.interestids = this.spatialUtilService.toIdsAttr(newInterestIds);
                        return polygonUpdate;
                    });
                if (polygonsToUpdate.length) {
                    return this.spatialUtilService.applyEdits('polygon', null, polygonsToUpdate, null);
                }
                return of(null);
            }),
            switchMap(() => {
                // Update polylines associated with previously deleted interests
                const polylinesToUpdate = this.spatialUtilService
                    .getPolylines()
                    .filter(p => {
                        const polylineInterestIds = this.spatialUtilService.parseIdsAttr(p.attributes.interestids);
                        return (
                            polylineInterestIds.some(id => !spatialInterestIds.includes(id)) ||
                            !spatialInterestIds.every(
                                id => polylineInterestIds.includes(id) === spatialInterestAssociationMap[id].polylineIds.includes(p.attributes.id)
                            )
                        );
                    })
                    .map(p => {
                        const newInterestIds = spatialInterestIds.filter(id =>
                            spatialInterestAssociationMap[id].polylineIds.includes(p.attributes.id)
                        );
                        const polylineUpdate = this.spatialUtilService.copyGraphic(p);
                        polylineUpdate.attributes.interestids = this.spatialUtilService.toIdsAttr(newInterestIds);
                        return polylineUpdate;
                    });
                if (polylinesToUpdate.length) {
                    return this.spatialUtilService.applyEdits('polyline', null, polylinesToUpdate, null);
                }
                return of(null);
            }),
            switchMap(() => {
                const interestsMap = {};

                spatialInterests.forEach(spatialInterest => {
                    interestsMap[`${spatialInterest.type}-${spatialInterest.interestid}`] = {
                        spatialInterest: spatialInterest
                    };
                });

                nonSpatialInterests.forEach(nonSpatialInterest => {
                    const k = `${nonSpatialInterest.type}-${nonSpatialInterest.interestId}`;
                    if (!interestsMap[k]) {
                        interestsMap[k] = {};
                    }
                    interestsMap[k].nonSpatialInterest = nonSpatialInterest;
                });

                Object.keys(interestsMap).forEach(k => {
                    const v = interestsMap[k];
                    if (
                        v.nonSpatialInterest &&
                        noActionNotRequiredNotifications.find(
                            ftn =>
                                ftn.identifier === v.nonSpatialInterest.tenure &&
                                (ftn.notificationCodeOverride || ftn.notificationCode) === v.nonSpatialInterest.interestcode &&
                                ftn.subjects.find(s => s.id === v.nonSpatialInterest.interestId)
                        )
                    ) {
                        if (v.spatialInterest) {
                            interestsToBeDeleted.push(v.spatialInterest);
                        }
                        return;
                    }

                    if (
                        v.spatialInterest &&
                        v.nonSpatialInterest &&
                        !this.spatialInterestEqualToNonSpatialInterest(v.spatialInterest, v.nonSpatialInterest)
                    ) {
                        interestsToBeUpdated.push(v.nonSpatialInterest);
                    } else if (v.spatialInterest && !v.nonSpatialInterest) {
                        interestsToBeDeleted.push(v.spatialInterest);
                    } else if (v.nonSpatialInterest && !v.spatialInterest) {
                        interestsToBeAdded.push(v.nonSpatialInterest);
                    }
                });

                let observable = of(null);

                if (interestsToBeDeleted.length) {
                    observable = observable.pipe(switchMap(() => this.interestService.deleteInterests(interestsToBeDeleted)));
                }

                if (interestsToBeAdded.length) {
                    observable = observable.pipe(switchMap(() => this.interestService.addInterests(interestsToBeAdded)));
                }

                if (interestsToBeUpdated.length) {
                    observable = observable.pipe(switchMap(() => this.interestService.updateInterests(interestsToBeUpdated)));
                }

                return observable;
            }),
            switchMap(() => this.spatialUtilService.deleteInvalidInterestAnnotations())
        );
    }
}
